/* ========================================================================== */
/*  FILE: components/home/MobileAppShowcaseClient.tsx                          */
/* ========================================================================== */

"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Lock,
  MapPin,
  QrCode,
  Search,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Timer,
  Zap,
} from "lucide-react";
import Link from "next/link";

type Screen = {
  id: string;
  title: string;
  description: string;
  image: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  kpiLabel: string;
  kpiValue: string;
};

export type MobileAppShowcaseClientProps = {
  heading?: string;
  subheading?: string;
  screens?: Screen[];
  autoplayMs?: number;
  initialIndex?: number;
  primaryCtaHref?: string;
  primaryCtaLabel?: string;
  secondaryCtaHref?: string;
  secondaryCtaLabel?: string;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

export default function MobileAppShowcaseClient(props: MobileAppShowcaseClientProps) {
  const reducedMotion = useReducedMotion();
  const autoplayMs = Math.max(2500, props.autoplayMs ?? 5000);

  const screens: Screen[] = useMemo(
    () =>
      props.screens ?? [
        {
          id: "discover",
          title: "Discover Restaurants",
          description:
            "Explore curated restaurants with detailed menus, photos, and reviews — built for Malawi.",
          image:
            "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=1200&auto=format&fit=crop",
          icon: Search,
          accent: "from-sky-500 to-cyan-500",
          kpiLabel: "Faster discovery",
          kpiValue: "2×",
        },
        {
          id: "order",
          title: "Order in Advance",
          description:
            "Place orders before arriving and skip the wait. Perfect for busy schedules.",
          image:
            "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?q=80&w=1200&auto=format&fit=crop",
          icon: ShoppingBag,
          accent: "from-amber-500 to-orange-500",
          kpiLabel: "Checkout time",
          kpiValue: "< 30s",
        },
        {
          id: "qr",
          title: "Scan & Order",
          description:
            "Scan a table QR code to open the menu instantly and order without delays.",
          image:
            "https://images.unsplash.com/photo-1525351484163-7529414344d8?q=80&w=1200&auto=format&fit=crop",
          icon: QrCode,
          accent: "from-fuchsia-500 to-pink-500",
          kpiLabel: "No waiting",
          kpiValue: "0 min",
        },
        {
          id: "track",
          title: "Track Live Orders",
          description:
            "Real-time status updates from kitchen to table service for a smoother experience.",
          image:
            "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop",
          icon: Timer,
          accent: "from-emerald-500 to-green-500",
          kpiLabel: "Live updates",
          kpiValue: "Instant",
        },
        {
          id: "pay",
          title: "Pay Securely",
          description:
            "Multiple payment options with secure, encrypted flows and digital receipts.",
          image:
            "https://images.unsplash.com/photo-1556740749-887f6717d7e4?q=80&w=1200&auto=format&fit=crop",
          icon: CreditCard,
          accent: "from-violet-500 to-indigo-500",
          kpiLabel: "Security",
          kpiValue: "Encrypted",
        },
      ],
    [props.screens]
  );

  const [active, setActive] = useState(() => {
    const idx = props.initialIndex ?? 0;
    return Math.min(Math.max(0, idx), Math.max(0, screens.length - 1));
  });

  const intervalRef = useRef<number | null>(null);
  const lastManualRef = useRef<number>(0);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (reducedMotion) return;
    if (screens.length <= 1) return;
    if (intervalRef.current) return;

    intervalRef.current = window.setInterval(() => {
      const now = Date.now();
      if (now - lastManualRef.current < 1500) return;
      setActive((i) => (i + 1) % screens.length);
    }, autoplayMs);
  }, [autoplayMs, reducedMotion, screens.length]);

  useEffect(() => {
    play();
    return () => stop();
  }, [play, stop]);

  const onNext = useCallback(() => {
    lastManualRef.current = Date.now();
    stop();
    setActive((i) => (i + 1) % screens.length);
  }, [screens.length, stop]);

  const onPrev = useCallback(() => {
    lastManualRef.current = Date.now();
    stop();
    setActive((i) => (i - 1 + screens.length) % screens.length);
  }, [screens.length, stop]);

  const heading = props.heading ?? "Your Perfect Dining Companion";
  const subheading =
    props.subheading ??
    "A sleek, fast, and secure mobile experience — designed for discovery, ordering, tracking, and payments.";

  const primaryCtaHref = props.primaryCtaHref ?? "/download";
  const primaryCtaLabel = props.primaryCtaLabel ?? "Download App";
  const secondaryCtaHref = props.secondaryCtaHref ?? "/vendors";
  const secondaryCtaLabel = props.secondaryCtaLabel ?? "Browse Restaurants";

  const ActiveIcon = screens[active]?.icon ?? Smartphone;
  const activeScreen = screens[active];

  return (
    <section className="relative py-18 sm:py-20 bg-gradient-to-b from-white to-amber-50" aria-label="Mobile app showcase">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(251,191,36,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(249,115,22,0.10),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2">
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4 rounded-full border border-amber-100 bg-white/70 px-4 py-2 backdrop-blur shadow-sm">
            <Smartphone className="h-4 w-4 text-amber-600" />
            <span className="text-xs sm:text-sm font-semibold text-amber-700 uppercase tracking-wide">
              Mobile app
            </span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-600 border border-neutral-200">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Feature-packed
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-neutral-900">
            {heading}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-neutral-600 leading-relaxed">
            {subheading}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left: screen list */}
          <div className="w-full lg:w-[42%]">
            <div className="space-y-4">
              {screens.map((s, idx) => {
                const isActive = idx === active;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      lastManualRef.current = Date.now();
                      stop();
                      setActive(idx);
                    }}
                    className={cn(
                      "w-full text-left rounded-2xl border transition-all duration-300",
                      isActive
                        ? "border-amber-200 bg-white shadow-xl"
                        : "border-neutral-200 bg-white/60 hover:bg-white hover:shadow-lg"
                    )}
                  >
                    <div className="p-5 sm:p-6 flex items-start gap-4">
                      <div
                        className={cn(
                          "h-12 w-12 rounded-2xl flex items-center justify-center text-white",
                          "bg-gradient-to-br",
                          s.accent
                        )}
                      >
                        <Icon className="h-6 w-6" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-base sm:text-lg font-extrabold text-neutral-900">
                              {s.title}
                            </h3>
                            <p className="mt-1 text-sm sm:text-base text-neutral-600">
                              {s.description}
                            </p>
                          </div>

                          {isActive ? (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700">
                              Active
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            </span>
                          ) : (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-3 py-1 text-xs font-semibold text-neutral-600">
                              Step {idx + 1}
                            </span>
                          )}
                        </div>

                        <div className="mt-3 flex items-center gap-3 text-xs text-neutral-600">
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-3 py-1">
                            <Zap className="h-3.5 w-3.5 text-amber-600" />
                            {s.kpiLabel}: <span className="font-bold">{s.kpiValue}</span>
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-3 py-1">
                            <Lock className="h-3.5 w-3.5 text-neutral-700" />
                            Secure
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                href={primaryCtaHref}
                className={cn(
                  "inline-flex flex-1 items-center justify-center gap-2 rounded-2xl px-6 py-4",
                  "bg-neutral-900 text-white font-extrabold shadow-lg hover:bg-neutral-800",
                  "transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                )}
              >
                <Smartphone className="h-5 w-5" />
                {primaryCtaLabel}
              </Link>
              <Link
                href={secondaryCtaHref}
                className={cn(
                  "inline-flex flex-1 items-center justify-center gap-2 rounded-2xl px-6 py-4",
                  "bg-white text-neutral-900 font-extrabold border border-neutral-200 shadow-sm hover:shadow-md",
                  "transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                )}
              >
                <MapPin className="h-5 w-5" />
                {secondaryCtaLabel}
              </Link>
            </div>

            <p className="mt-3 text-center sm:text-left text-xs text-neutral-500">
              Free to download • No hidden fees • Optimized for low-data connections
            </p>
          </div>

          {/* Right: phone preview */}
          <div className="w-full lg:w-[58%]">
            <div
              className="relative mx-auto max-w-md"
              onMouseEnter={() => {
                lastManualRef.current = Date.now();
                stop();
              }}
              onMouseLeave={() => {
                lastManualRef.current = Date.now();
                play();
              }}
            >
              {/* Decorative backplates */}
              <div className="pointer-events-none absolute -inset-10 rounded-[3rem] bg-gradient-to-r from-amber-200/30 via-white/0 to-orange-200/30 blur-2xl" />
              <div className="pointer-events-none absolute -inset-16 rounded-[3rem] bg-gradient-to-r from-amber-100/20 via-white/0 to-orange-100/20 blur-3xl" />

              {/* Phone frame */}
              <div className="relative rounded-[2.8rem] bg-gradient-to-br from-neutral-950 to-black p-4 shadow-2xl">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 h-6 w-36 rounded-b-3xl bg-black" />

                <div className="relative aspect-[9/19.5] overflow-hidden rounded-[2.2rem] border border-white/10">
                  {activeScreen?.image ? (
                    <>
                      <Image
                        src={activeScreen.image}
                        alt={activeScreen.title}
                        fill
                        sizes="(max-width: 1024px) 90vw, 420px"
                        className="object-cover"
                        priority={false}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
                    </>
                  ) : null}

                  {/* Overlay card */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-xl p-4 shadow-xl">
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "h-11 w-11 rounded-2xl flex items-center justify-center text-white",
                            "bg-gradient-to-br",
                            activeScreen?.accent ?? "from-amber-500 to-orange-500"
                          )}
                        >
                          <ActiveIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-extrabold text-base">
                            {activeScreen?.title ?? "Explore IndeMenu"}
                          </div>
                          <div className="mt-1 text-white/80 text-sm">
                            {activeScreen?.description ?? "Discover, order, and pay faster."}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-xs text-white/70">
                          Step {active + 1} of {screens.length}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={onPrev}
                            className="rounded-full bg-white/15 hover:bg-white/20 p-2 text-white transition-colors"
                            aria-label="Previous screen"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={onNext}
                            className="rounded-full bg-white/15 hover:bg-white/20 p-2 text-white transition-colors"
                            aria-label="Next screen"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* micro KPIs */}
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                          <div className="text-[11px] text-white/70">KPI</div>
                          <div className="text-sm font-extrabold text-white">
                            {activeScreen?.kpiValue ?? "Fast"}
                          </div>
                        </div>
                        <div className="rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                          <div className="text-[11px] text-white/70">Secure</div>
                          <div className="text-sm font-extrabold text-white">Encrypted</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Home indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 h-1.5 w-28 rounded-full bg-white/20" />
              </div>

              {/* Indicators */}
              <div className="mt-7 flex items-center justify-center gap-2">
                {screens.map((_, idx) => {
                  const isActive = idx === active;
                  return (
                    <button
                      key={`screen-dot-${idx}`}
                      type="button"
                      onClick={() => {
                        lastManualRef.current = Date.now();
                        stop();
                        setActive(idx);
                      }}
                      className={cn(
                        "h-2.5 rounded-full transition-all duration-300",
                        isActive ? "w-10 bg-amber-500" : "w-2.5 bg-neutral-300 hover:bg-neutral-400"
                      )}
                      aria-label={`Go to screen ${idx + 1}`}
                      aria-pressed={isActive}
                    />
                  );
                })}
              </div>

              {/* Quick stat bar */}
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm">
                  <div className="text-xl font-extrabold text-neutral-900">4.9★</div>
                  <div className="text-xs text-neutral-600">Store rating</div>
                </div>
                <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm">
                  <div className="text-xl font-extrabold text-neutral-900">100K+</div>
                  <div className="text-xs text-neutral-600">Downloads</div>
                </div>
                <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm">
                  <div className="text-xl font-extrabold text-neutral-900">98%</div>
                  <div className="text-xs text-neutral-600">Satisfaction</div>
                </div>
                <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm">
                  <div className="text-xl font-extrabold text-neutral-900">&lt;2s</div>
                  <div className="text-xs text-neutral-600">Load time</div>
                </div>
              </div>

              <p className="mt-3 text-center text-xs text-neutral-500">
                Autoplay respects your device motion preferences.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
